import { PhosphorIconName } from "@storeblocks/icon-ng";
export enum ContractCategory {
  Employer = "EMPLOYER",
  UserSavings = "USER_SAVINGS",
  Folketrygden = "FOLKETRYGDEN",
}

/**
 * Backend `SavingsEngagementType` names served as `productType` on /contracts/summary.
 * Only the values the frontend acts on; the backend can send others.
 */
export enum ProductType {
  Epk = "EPK",
  EpkFleksibel = "EPK_FLEKSIBEL",
  Pkb = "PKB",
  EpkFleksibelPkb = "EPK_FLEKSIBEL_PKB",
}

export enum ActionId {
  MovePension = "MOVE_PENSION",
  AddContract = "ADD_CONTRACT",
  ChangeInvestment = "CHANGE_INVESTMENT",
  ChangeSavingsAgreement = "CHANGE_SAVINGS_AGREEMENT",
  StartSavingsAgreement = "START_SAVINGS_AGREEMENT",
  DepositMoney = "DEPOSIT_MONEY",
  WithdrawMoney = "WITHDRAW_MONEY",
  CreateSavingsAgreement = "CREATE_SAVINGS_AGREEMENT",
}

export function getActionIcon(actionId: ActionId): PhosphorIconName {
  switch (actionId) {
    case ActionId.MovePension:
      return "arrows-merge";
    case ActionId.DepositMoney:
      return "arrow-line-left";
    case ActionId.ChangeInvestment:
      return "chart-line";
    case ActionId.WithdrawMoney:
      return "arrow-line-down";
    case ActionId.ChangeSavingsAgreement:
      return "calendar-dot";
    case ActionId.StartSavingsAgreement:
      return "calendar-dots";
    case ActionId.AddContract:
      return "arrows-down-up";
    case ActionId.CreateSavingsAgreement:
      return "plus";
  }
}

export function getContractIcon(type: ContractCategory | null): PhosphorIconName | "" {
  if (type === ContractCategory.Employer) {
    return "building";
  }
  if (type === ContractCategory.UserSavings) {
    return "user";
  }
  if (type === ContractCategory.Folketrygden) {
    return "";
  }
  return "";
}

export function getContractTypeLabel(type: ContractCategory | null): string {
  if (type === ContractCategory.Employer) {
    return "Arbeidsgiver";
  }
  if (type === ContractCategory.UserSavings) {
    return "Fondssparing";
  }
  if (type === ContractCategory.Folketrygden) {
    return "Folketrygd";
  }
  return "Annen sparing";
}

export interface ActionListItemModel {
  id: string;
  name: string;
  type: ContractCategory | null;
  category?: string | null;
  navigationLink: string | null;
  totalBalance?: number | null;
  totalReturn?: number | null;
  nextDepositDate?: string | null;
  monthlyDepositAmount?: number | null;
}

export interface ContractGroupModel {
  groupType: string;
  contracts: ActionListItemModel[];
}

export interface ActionModel {
  id: ActionId;
  name: string;
  detailsLink: string | null;
  contracts: ActionListItemModel[] | null;
  contractGroups?: ContractGroupModel[] | null;
}

const CONTRACT_GROUP_TYPE_LABELS: Record<string, string> = {
  FUND_BASED_PENSIONABLE: "contractsPage.contractGroup.fundBased",
  BANK_BASED_PENSIONABLE: "contractsPage.contractGroup.bankBased",
};

export function getContractGroupTypeLabel(groupType: string): string {
  return CONTRACT_GROUP_TYPE_LABELS[groupType] ?? groupType;
}

/**
 * Returns an action's selectable contracts as a flat list.
 *
 * Actions expose their contracts in one of two shapes:
 * - a flat `contracts` array (most actions), or
 * - a `contractGroups` array grouped by `groupType` (e.g. ADD_CONTRACT).
 *
 * When only `contractGroups` is present, the grouped contracts are flattened and
 * each item's `category` is set to the group's translated label so the
 * select-contract modal can render them under group headings.
 */
export function getActionContractItems(action: ActionModel): ActionListItemModel[] {
  if (action.contracts && action.contracts.length > 0) {
    return action.contracts;
  }

  return (action.contractGroups ?? []).flatMap((group) =>
    group.contracts.map((contract) => ({
      ...contract,
      category: getContractGroupTypeLabel(group.groupType),
    })),
  );
}

/**
 * Determines whether an action has anything actionable to render — either an
 * external details link or at least one selectable contract (flat or grouped).
 */
export function hasActionContent(action: ActionModel): boolean {
  return action.detailsLink !== null || getActionContractItems(action).length > 0;
}

export interface ContractOptionsModel {
  isMoved?: boolean;
  isPaidUp?: boolean;
  isKron?: boolean;
}

export interface ContractModel {
  id: string;
  name: string;
  subtitle: string;
  detailsLink: string | null;
  type: ContractCategory | null;
  contractOptions?: ContractOptionsModel | null;
  totalBalance: number | null;
  totalReturn: number | null;
  inTransit: boolean;
}

export interface ContractPartialError {
  titleKey?: string;
  message?: string;
  messageKey?: string;
}

export interface ContractResponse {
  contracts: ContractModel[];
  partialError?: ContractPartialError;
}
